import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Star, MessageSquare } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSurveyPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);   // conversational one-question-at-a-time
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/fb/public/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("This survey isn't available."));
  }, [token]);

  if (err) return <Shell><div className="text-center text-slate-600">{err}</div></Shell>;
  if (!data) return <Shell><Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto" /></Shell>;

  const survey = data.survey;
  const q = survey.questions[step];
  const total = survey.questions.length;

  const set = (v) => setAnswers({ ...answers, [q.id]: v });
  const next = () => setStep(step + 1);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = Object.entries(answers).map(([qid, a]) => {
        if (typeof a === "object" && a !== null && "text" in a) return { qid, text: a.text };
        if (typeof a === "boolean") return { qid, value: a };
        return { qid, value: a };
      });
      await axios.post(`${API}/fb/public/${token}/respond`, {
        answers: payload,
        visitor_meta: { url: window.location.href },
      });
      setSubmitted(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (submitted) return (
    <Shell>
      <div className="text-center max-w-md mx-auto" data-testid="thankyou">
        <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-slate-900">{survey.thank_you_message || "Thank you!"}</h1>
        <p className="mt-2 text-sm text-slate-600">Your feedback helps us improve.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="max-w-xl mx-auto" data-testid="public-survey">
        <div className="text-center mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">{survey.title}</div>
          {survey.intro && <p className="mt-2 text-sm text-slate-600">{survey.intro}</p>}
          <div className="mt-3 text-[11px] text-slate-400">Question {step + 1} of {total}</div>
          <div className="mt-2 h-1 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900" data-testid={`q-text-${step}`}>{q.text}</h2>
          <div className="mt-4">
            <Renderer q={q} value={answers[q.id]} onChange={set} />
          </div>
          {err && <div className="mt-3 text-xs text-rose-600" data-testid="submit-error">{err}</div>}
          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              className="text-sm text-slate-500 hover:text-slate-800 disabled:opacity-40" data-testid="back-btn">← Back</button>
            {step < total - 1 ? (
              <button onClick={next} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 px-4 py-2 text-sm font-semibold" data-testid="next-btn">Next →</button>
            ) : (
              <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 px-4 py-2 text-sm font-semibold" data-testid="submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit
              </button>
            )}
          </div>
        </div>
        <div className="mt-4 text-center text-[11px] text-slate-400">Powered by WebsiteTech Customer Voice AI</div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-slate-50 grid place-items-center p-6">{children}</div>;
}

function Renderer({ q, value, onChange }) {
  if (q.type === "csat" || q.type === "star" || q.type === "smiley") {
    const max = 5;
    return (
      <div className="flex gap-2 justify-center">
        {Array.from({ length: max }).map((_, i) => {
          const v = i + 1;
          const active = value === v;
          return (
            <button key={v} onClick={() => onChange(v)} data-testid={`opt-${v}`}
              className={`w-12 h-12 rounded-full border-2 grid place-items-center transition ${active ? "border-emerald-500 bg-emerald-100" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`text-lg font-bold ${active ? "text-emerald-700" : "text-slate-500"}`}>{v}</span>
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === "nps") {
    return (
      <div className="grid grid-cols-11 gap-1">
        {Array.from({ length: 11 }).map((_, v) => (
          <button key={v} onClick={() => onChange(v)} data-testid={`opt-${v}`}
            className={`h-10 rounded border text-sm font-semibold transition ${value === v ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>{v}</button>
        ))}
      </div>
    );
  }
  if (q.type === "ces") {
    return (
      <div className="flex gap-2 justify-center flex-wrap">
        {[1, 2, 3, 4, 5, 6, 7].map((v) => (
          <button key={v} onClick={() => onChange(v)} data-testid={`opt-${v}`}
            className={`w-10 h-10 rounded border text-sm font-semibold transition ${value === v ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200"}`}>{v}</button>
        ))}
      </div>
    );
  }
  if (q.type === "yes_no" || q.type === "consent") {
    return (
      <div className="flex gap-3 justify-center">
        <button onClick={() => onChange(true)} data-testid="opt-yes"
          className={`px-6 py-3 rounded-lg border font-semibold ${value === true ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300"}`}>Yes</button>
        <button onClick={() => onChange(false)} data-testid="opt-no"
          className={`px-6 py-3 rounded-lg border font-semibold ${value === false ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-300"}`}>No</button>
      </div>
    );
  }
  if (q.type === "single_choice" || q.type === "dropdown") {
    return (
      <div className="flex flex-col gap-2">
        {(q.options || []).map((o, i) => (
          <button key={i} onClick={() => onChange(o)} data-testid={`opt-${i}`}
            className={`px-4 py-3 rounded-lg border text-left text-sm font-semibold ${value === o ? "bg-emerald-50 text-emerald-800 border-emerald-500" : "bg-white text-slate-700 border-slate-300"}`}>{o}</button>
        ))}
      </div>
    );
  }
  if (q.type === "multi_choice") {
    const current = value || [];
    const toggle = (o) => onChange(current.includes(o) ? current.filter((x) => x !== o) : [...current, o]);
    return (
      <div className="flex flex-col gap-2">
        {(q.options || []).map((o, i) => (
          <button key={i} onClick={() => toggle(o)} data-testid={`opt-${i}`}
            className={`px-4 py-3 rounded-lg border text-left text-sm font-semibold ${current.includes(o) ? "bg-emerald-50 text-emerald-800 border-emerald-500" : "bg-white text-slate-700 border-slate-300"}`}>{o}</button>
        ))}
      </div>
    );
  }
  if (q.type === "short_text") {
    return <input value={value?.text || ""} onChange={(e) => onChange({ text: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm" data-testid="opt-text" />;
  }
  if (q.type === "long_text") {
    return <textarea rows={5} value={value?.text || ""} onChange={(e) => onChange({ text: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm" data-testid="opt-text" />;
  }
  if (q.type === "number") {
    return <input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm" data-testid="opt-number" />;
  }
  if (q.type === "date") {
    return <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm" data-testid="opt-date" />;
  }
  return <textarea rows={3} value={value?.text || ""} onChange={(e) => onChange({ text: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm" data-testid="opt-text" />;
}
